/** Formatting shared by the telemetry-lake views. UTC is the lake's own clock. */

export type TelemetryTz = "local" | "utc";

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !isFinite(bytes)) return "—";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

/** Athena bills fractions of a cent per query here; rounding to $0.00 hides that. */
export function formatUsd(usd: number | null | undefined): string {
  if (usd === null || usd === undefined || !isFinite(usd)) return "—";
  if (usd === 0) return "$0";
  if (usd < 0.01) return `$${usd.toFixed(5)}`;
  return `$${usd.toFixed(2)}`;
}

export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !isFinite(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) return "—";
  const abs = Math.abs(Math.round(seconds));
  if (abs < 60) return `${abs}s`;
  if (abs < 3600) return `${Math.floor(abs / 60)}m ${abs % 60}s`;
  const hours = Math.floor(abs / 3600);
  const minutes = Math.floor((abs % 3600) / 60);
  if (hours < 24) return `${hours}h ${minutes}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function formatTs(iso: string | null | undefined, tz: TelemetryTz = "local"): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return String(iso);
  if (tz === "utc") {
    return date.toISOString().replace("T", " ").replace(/\.\d+Z$/, "Z");
  }
  return date.toLocaleString([], { dateStyle: "short", timeStyle: "medium" });
}

export function formatTimeOnly(iso: string | null | undefined, tz: TelemetryTz = "local"): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return String(iso);
  if (tz === "utc") return date.toISOString().slice(11, 19) + "Z";
  return date.toLocaleTimeString([], { hour12: false });
}

/** "12m ago" for a point in the past; the lake's lag is always the point. */
export function formatAge(iso: string | null | undefined): string {
  if (!iso) return "unknown";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "unknown";
  return `${formatSeconds((Date.now() - date.getTime()) / 1000)} ago`;
}

export function formatNumber(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || !isFinite(value)) return "—";
  return value.toFixed(decimals);
}

/** ISO-8601 with Z — the only thing the API accepts. */
export function toIsoZ(date: Date): string {
  return date.toISOString().replace(/\.\d+Z$/, "Z");
}

/** `<input type="datetime-local">` value for an instant, in browser-local time. */
export function toLocalInputValue(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

export function fromLocalInputValue(value: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return toIsoZ(date);
}

/**
 * Lag with its sign kept.
 *
 * `formatSeconds` is deliberately absolute — every other caller formats a
 * duration, where a sign would be noise. The lake watermark is the one place
 * where the sign is the whole message: a watermark *ahead* of now is not the
 * lake being |x| behind, it is the feeder reading naive producer timestamps in
 * the wrong timezone (see apps/telemetry_file_archiver/RUNBOOK.md §"Timezone").
 * Rendering that as "7h behind" turns a config bug into a plausible-looking
 * backlog, so freshness goes through here instead of through `formatSeconds`.
 */
export type LakeLag =
  | { kind: "unknown"; text: string }
  | { kind: "fresh"; text: string }
  | { kind: "behind"; text: string; behindSeconds: number }
  | { kind: "future"; text: string; aheadSeconds: number };

/**
 * Ordinary clock skew between the feeder host and this browser. Below this a
 * negative lag is just two clocks disagreeing, not a timezone mismatch — those
 * are whole hours off.
 */
export const LAG_SKEW_TOLERANCE_S = 60;

export function describeLag(seconds: number | null | undefined): LakeLag {
  if (seconds === null || seconds === undefined || !isFinite(seconds)) {
    return { kind: "unknown", text: "lag unknown" };
  }
  if (seconds < -LAG_SKEW_TOLERANCE_S) {
    const ahead = -seconds;
    return {
      kind: "future",
      aheadSeconds: ahead,
      text: `watermark is ${formatSeconds(ahead)} in the future — check feeder source timezone`,
    };
  }
  if (seconds <= 0) return { kind: "fresh", text: "up to date" };
  return { kind: "behind", behindSeconds: seconds, text: `${formatSeconds(seconds)} behind` };
}
